import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { 
  History, 
  Star, 
  Clock, 
  MousePointer2, 
  Image as ImageIcon, 
  Trophy,
  CheckCircle2,
  Play,
  Grid3X3,
  ArrowLeft
} from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { DIFFICULTIES } from '../../lib/constants';
import { cn } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function HistoryModal({ isOpen, onClose, deviceId, onPlayImage }) {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' | 'list'
  
  // Group history by unique images and count plays
  const uniquePuzzles = React.useMemo(() => {
    const grouped = {};
    history.forEach(entry => {
      if (!grouped[entry.image_url]) {
        grouped[entry.image_url] = {
          ...entry,
          play_count: 1,
          best_score: entry.score,
          has_perfect: entry.perfect_game
        };
      } else {
        grouped[entry.image_url].play_count += 1;
        if (entry.score > grouped[entry.image_url].best_score) {
          grouped[entry.image_url].best_score = entry.score;
        }
        if (entry.perfect_game) {
          grouped[entry.image_url].has_perfect = true;
        }
      }
    });
    // Sort by most recent (first entry in history is most recent)
    return Object.values(grouped);
  }, [history]);
  
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [historyRes, statsRes] = await Promise.all([
        axios.get(`${API}/images/history/${deviceId}?limit=100`),
        axios.get(`${API}/images/stats/${deviceId}`)
      ]);
      setHistory(historyRes.data.history || []);
      setStats(statsRes.data);
    } catch (err) {
      logger.error('Failed to fetch history:', err);
      setError('Failed to load puzzle history');
    } finally {
      setIsLoading(false);
    }
  }, [deviceId]);
  
  useEffect(() => {
    if (isOpen && deviceId) {
      fetchHistory();
    }
  }, [isOpen, deviceId, fetchHistory]);
  
  const getDifficultyBadge = (difficulty) => {
    const config = DIFFICULTIES[difficulty] || DIFFICULTIES.normal;
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "text-xs px-1.5 py-0",
          difficulty === 'easy' && "bg-success/10 text-success border-success/30",
          difficulty === 'normal' && "bg-primary/10 text-primary border-primary/30",
          difficulty === 'hard' && "bg-destructive/10 text-destructive border-destructive/30"
        )}
      >
        {config.badge}
      </Badge>
    );
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto box-border" data-testid="history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <History className="h-5 w-5 text-primary" />
            Puzzle History
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="space-y-4">
            {/* Stats skeleton */}
            <div className="grid grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
            {/* Gallery skeleton */}
            <div className="grid grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-lg" />
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive" data-testid="history-error">
            {error}
            <Button variant="link" onClick={fetchHistory} className="block mx-auto mt-2">
              Try again
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="history-stats">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-xs">Total Plays</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">
                    {stats.total_plays}
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <ImageIcon className="h-4 w-4" />
                    <span className="text-xs">Puzzles</span>
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {stats.unique_images_completed}
                    <span className="text-sm text-muted-foreground font-normal">/{stats.total_available_images}</span>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <Star className="h-4 w-4" />
                    <span className="text-xs">Perfect</span>
                  </div>
                  <div className="text-2xl font-bold text-warning">
                    {stats.perfect_games}
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                    <Trophy className="h-4 w-4" />
                    <span className="text-xs">Avg Score</span>
                  </div>
                  <div className="text-2xl font-bold text-success">
                    {Math.round(stats.average_score)}
                  </div>
                </div>
              </div>
            )}
            
            {/* View Toggle */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-muted-foreground">
                Unique Puzzles
              </h3>
              <div className="flex gap-1">
                <Button
                  variant={viewMode === 'gallery' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('gallery')}
                  data-testid="view-gallery-btn"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-btn"
                >
                  <History className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* History Content */}
            {uniquePuzzles.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="history-empty">
                <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No puzzles completed yet</p>
                <p className="text-sm mt-1">Start playing to build your history!</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-2">
                {viewMode === 'gallery' ? (
                  /* Gallery View */
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="history-gallery">
                    {uniquePuzzles.map((entry) => (
                      <div 
                        key={entry.image_url}
                        className="group relative aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                        onClick={() => {
                          onPlayImage?.(entry.image_url);
                          onClose();
                        }}
                        data-testid={`history-item-${entry.image_url.slice(-8)}`}
                      >
                        <img 
                          src={entry.image_url} 
                          alt="Puzzle"
                          className="w-full h-full object-cover"
                        />
                        {/* Play count badge */}
                        <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded-full">
                          {entry.play_count}x
                        </div>
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-medium">{Math.round(entry.best_score)}</span>
                              {entry.has_perfect && <Star className="h-3 w-3 text-warning fill-warning" />}
                            </div>
                          </div>
                        </div>
                        {/* Perfect badge */}
                        {entry.has_perfect && (
                          <div className="absolute top-1 right-1">
                            <Star className="h-4 w-4 text-warning fill-warning drop-shadow-lg" />
                          </div>
                        )}
                        {/* Play overlay */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-primary/90 rounded-full p-2">
                            <Play className="h-5 w-5 text-white" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* List View */
                  <div className="space-y-2" data-testid="history-list">
                    {uniquePuzzles.map((entry, index) => (
                      <div 
                        key={entry.image_url}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => {
                          onPlayImage?.(entry.image_url);
                          onClose();
                        }}
                        data-testid={`history-list-item-${entry.image_url.slice(-8)}`}
                      >
                        {/* Thumbnail */}
                        <div className="relative w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                          <img 
                            src={entry.image_url} 
                            alt={`Puzzle ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {entry.has_perfect && (
                            <div className="absolute top-0.5 right-0.5">
                              <Star className="h-3 w-3 text-warning fill-warning drop-shadow-lg" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">
                              {Math.round(entry.best_score)}
                            </span>
                            {getDifficultyBadge(entry.difficulty)}
                            {entry.has_perfect && (
                              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-xs px-1.5 py-0">
                                Perfect!
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              {entry.play_count}x played
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(entry.time_seconds)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MousePointer2 className="h-3 w-3" />
                              {entry.moves} moves
                            </span>
                            <span>{formatDate(entry.completed_at)}</span>
                          </div>
                        </div>
                        
                        {/* Play button */}
                        <Button variant="ghost" size="sm" className="flex-shrink-0">
                          <Play className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
